package com.havn.app.features.tasks.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Checkbox
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.features.tasks.domain.AiSuggestedTask
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskDraft
import com.havn.app.features.tasks.domain.HavnTaskPriority
import com.havn.app.features.tasks.domain.HavnTaskStatus
import com.havn.app.features.tasks.domain.HavnTaskType
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@Composable
fun TasksScreen(
    viewModel: TasksViewModel,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    agencyViewModel: AgencyViewModel,
    onBackToToday: (() -> Unit)? = null
) {
    val state by viewModel.uiState.collectAsState()
    val propertiesState by propertiesViewModel.uiState.collectAsState()
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val leadsState by leadsViewModel.uiState.collectAsState()
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val theme = LocalHavnThemeTokens.current
    var filter by remember { mutableStateOf(TaskFilter.Today) }

    val context = remember(
        propertiesState.properties,
        openHousesState.openHouses,
        leadsState.leads,
        followUpsState.followUps,
        agencyState.members
    ) {
        TaskContextIndex(
            properties = propertiesState.properties.associateBy { it.id },
            openHouses = openHousesState.openHouses.associateBy { it.id },
            leads = leadsState.leads.associateBy { it.id },
            followUps = followUpsState.followUps.associateBy { it.id },
            members = agencyState.members.associateBy { it.agentId }
        )
    }
    val suggestedTasks = remember(leadsState.leads, openHousesState.openHouses, followUpsState.followUps, state.dismissedSuggestionIds, agencyState.selectedAgencyId) {
        buildAiSuggestions(
            leads = leadsState.leads,
            openHouses = openHousesState.openHouses,
            followUps = followUpsState.followUps,
            agencyId = agencyState.selectedAgencyId,
            dismissedIds = state.dismissedSuggestionIds
        )
    }
    val filteredTasks = state.tasks.filterBy(filter)
    val selectedTask = state.tasks.firstOrNull { it.id == state.selectedTaskId } ?: filteredTasks.firstOrNull()

    HavnScreenFrame(
        eyebrow = "",
        title = "Tasks",
        subtitle = "",
        backLabel = onBackToToday?.let { "Today" },
        onBack = onBackToToday,
        isRefreshing = state.isLoading || propertiesState.isLoading || openHousesState.isLoading || leadsState.isLoading || followUpsState.isLoading,
        onRefresh = {
            viewModel.refresh()
            propertiesViewModel.refresh()
            openHousesViewModel.refresh()
            leadsViewModel.refresh()
            followUpsViewModel.refresh()
            agencyViewModel.refresh()
        }
    ) {
        BoxWithConstraints(
            Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.background, theme.gradientStart, theme.background)))
        ) {
            val wide = havnIsWebShell && maxWidth >= 1040.dp
            Column(verticalArrangement = Arrangement.spacedBy(14.dp), modifier = Modifier.fillMaxWidth()) {
                TaskFilters(
                    selected = filter,
                    tasks = state.tasks,
                    suggestionCount = suggestedTasks.size,
                    wide = wide,
                    onSelected = { filter = it }
                )
                if (state.errorMessage != null) {
                    Text(state.errorMessage.orEmpty(), color = theme.danger, style = MaterialTheme.typography.bodyMedium)
                }
                if (wide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        TaskListColumn(
                            tasks = filteredTasks,
                            selectedTaskId = selectedTask?.id,
                            context = context,
                            emptyText = filter.emptyText,
                            onSelect = viewModel::selectTask,
                            onToggleDone = { task ->
                                if (task.status == HavnTaskStatus.Done) viewModel.reopen(task.id) else viewModel.markDone(task.id)
                            },
                            modifier = Modifier.weight(1.15f)
                        )
                        Column(modifier = Modifier.weight(0.9f), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            AiSuggestionsPanel(
                                suggestions = suggestedTasks,
                                context = context,
                                onAccept = viewModel::acceptSuggestedTask,
                                onDismiss = viewModel::dismissSuggestion
                            )
                            TaskDetailScreen(
                                task = selectedTask,
                                context = context,
                                onMarkComplete = { selectedTask?.let { viewModel.markDone(it.id) } },
                                onReopen = { selectedTask?.let { viewModel.reopen(it.id) } }
                            )
                        }
                    }
                } else {
                    AiSuggestionsPanel(
                        suggestions = suggestedTasks,
                        context = context,
                        onAccept = viewModel::acceptSuggestedTask,
                        onDismiss = viewModel::dismissSuggestion
                    )
                    TaskListColumn(
                        tasks = filteredTasks,
                        selectedTaskId = selectedTask?.id,
                        context = context,
                        emptyText = filter.emptyText,
                        onSelect = viewModel::selectTask,
                        onToggleDone = { task ->
                            if (task.status == HavnTaskStatus.Done) viewModel.reopen(task.id) else viewModel.markDone(task.id)
                        },
                        modifier = Modifier.fillMaxWidth()
                    )
                    TaskDetailScreen(
                        task = selectedTask,
                        context = context,
                        onMarkComplete = { selectedTask?.let { viewModel.markDone(it.id) } },
                        onReopen = { selectedTask?.let { viewModel.reopen(it.id) } }
                    )
                }
            }
        }
    }
}

@Composable
private fun TaskKpiRow(tasks: List<HavnTask>, suggestionCount: Int, wide: Boolean) {
    val today = tasks.count { it.isToday() && it.status != HavnTaskStatus.Done }
    val overdue = tasks.count { it.isOverdue() && it.status != HavnTaskStatus.Done }
    val high = tasks.count { it.priority == HavnTaskPriority.High || it.priority == HavnTaskPriority.Urgent }
    if (wide) {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            TaskKpiCard("Today", today.toString(), HavnIconKey.TodayGrid, Modifier.weight(1f))
            TaskKpiCard("Overdue", overdue.toString(), HavnIconKey.CalendarClock, Modifier.weight(1f))
            TaskKpiCard("High Priority", high.toString(), HavnIconKey.Star, Modifier.weight(1f))
            TaskKpiCard("AI Suggested", suggestionCount.toString(), HavnIconKey.Reports, Modifier.weight(1f))
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                TaskKpiCard("Today", today.toString(), HavnIconKey.TodayGrid, Modifier.weight(1f))
                TaskKpiCard("Overdue", overdue.toString(), HavnIconKey.CalendarClock, Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                TaskKpiCard("High Priority", high.toString(), HavnIconKey.Star, Modifier.weight(1f))
                TaskKpiCard("AI Suggested", suggestionCount.toString(), HavnIconKey.Reports, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun TaskKpiCard(title: String, value: String, icon: HavnIconKey, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier, border = BorderStroke(1.dp, theme.border)) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                HavnIcon(icon, contentDescription = title, tint = theme.primaryAccent, modifier = Modifier.size(19.dp))
                Text(title, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
        }
    }
}

@Composable
private fun TaskFilters(
    selected: TaskFilter,
    tasks: List<HavnTask>,
    suggestionCount: Int,
    wide: Boolean,
    onSelected: (TaskFilter) -> Unit
) {
    val filters = TaskFilter.entries
    val chipContent = @Composable { filter: TaskFilter ->
        FilterChip(
            label = filter.label,
            count = filter.countFor(tasks, suggestionCount),
            selected = selected == filter,
            onClick = { onSelected(filter) }
        )
    }
    if (wide) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            filters.forEach { filter -> chipContent(filter) }
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                filters.take(3).forEach { filter ->
                    Box(Modifier.weight(1f)) { chipContent(filter) }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                filters.drop(3).forEach { filter ->
                    Box(Modifier.weight(1f)) { chipContent(filter) }
                }
            }
        }
    }
}

@Composable
private fun FilterChip(label: String, count: Int, selected: Boolean, onClick: () -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = if (selected) theme.primaryAccent.copy(alpha = 0.18f) else theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border),
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 10.dp, vertical = 9.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                "$label $count",
                color = if (selected) theme.primaryAccent else theme.secondaryText,
                style = MaterialTheme.typography.labelLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun TaskListColumn(
    tasks: List<HavnTask>,
    selectedTaskId: String?,
    context: TaskContextIndex,
    emptyText: String,
    onSelect: (String) -> Unit,
    onToggleDone: (HavnTask) -> Unit,
    modifier: Modifier
) {
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("TASK QUEUE", color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleMedium)
            if (tasks.isEmpty()) {
                Text(emptyText, color = LocalHavnThemeTokens.current.secondaryText, style = MaterialTheme.typography.bodyLarge)
            }
            tasks.forEach { task ->
                TaskCard(
                    task = task,
                    selected = task.id == selectedTaskId,
                    context = context,
                    onClick = { onSelect(task.id) },
                    onToggleDone = { onToggleDone(task) }
                )
            }
        }
    }
}

@Composable
private fun TaskCard(
    task: HavnTask,
    selected: Boolean,
    context: TaskContextIndex,
    onClick: () -> Unit,
    onToggleDone: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = if (selected) theme.primaryAccent.copy(alpha = 0.12f) else theme.surface,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick)
    ) {
        Row(modifier = Modifier.padding(12.dp), horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
            Checkbox(checked = task.status == HavnTaskStatus.Done, onCheckedChange = { onToggleDone() })
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text(task.title, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                    TaskPriorityBadge(task.priority)
                    if (task.aiGenerated) AiBadge()
                }
                Text(task.description ?: task.type.label, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium, maxLines = 2, overflow = TextOverflow.Ellipsis)
                TaskQuickActionRow(task, context)
            }
        }
    }
}

@Composable
private fun TaskQuickActionRow(task: HavnTask, context: TaskContextIndex) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(9.dp), verticalAlignment = Alignment.CenterVertically) {
        HavnIcon(HavnIconKey.Calendar, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(15.dp))
        Text(task.dueAt?.readableDateTime() ?: "No due date", color = theme.secondaryText, style = MaterialTheme.typography.labelMedium)
        context.propertyLabel(task)?.let { Text(it, color = theme.primaryAccent, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis) }
        context.leadLabel(task)?.let { Text(it, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis) }
        context.memberLabel(task.assignedToUserId)?.let { Text(it, color = theme.mutedText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis) }
    }
}

@Composable
private fun AiSuggestionsPanel(
    suggestions: List<AiSuggestedTask>,
    context: TaskContextIndex,
    onAccept: (HavnTaskDraft) -> Unit,
    onDismiss: (String) -> Unit
) {
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("AI SUGGESTED TASKS", color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleMedium)
            if (suggestions.isEmpty()) {
                Text("No AI suggestions yet. HAVN will suggest tasks after open houses and lead activity.", color = LocalHavnThemeTokens.current.secondaryText, style = MaterialTheme.typography.bodyMedium)
            }
            suggestions.take(5).forEach { suggestion ->
                AiSuggestedTaskCard(suggestion, context, onAccept, onDismiss)
            }
        }
    }
}

@Composable
private fun AiSuggestedTaskCard(
    suggestion: AiSuggestedTask,
    context: TaskContextIndex,
    onAccept: (HavnTaskDraft) -> Unit,
    onDismiss: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.card, shape = MaterialTheme.shapes.medium, border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.45f))) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
                Text(suggestion.draft.title, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, modifier = Modifier.weight(1f))
                TaskPriorityBadge(suggestion.draft.priority)
            }
            Text(suggestion.reason, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            TaskContextCard(
                property = context.properties[suggestion.draft.propertyId],
                openHouse = context.openHouses[suggestion.draft.openHouseId],
                lead = context.leads[suggestion.draft.leadId]
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                HavnButton("Accept", onClick = { onAccept(suggestion.draft) }, leadingIcon = HavnIconKey.Check, modifier = Modifier.weight(1f))
                HavnButton("Dismiss", onClick = { onDismiss(suggestion.id) }, style = HavnButtonStyle.Secondary, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun TaskDetailScreen(
    task: HavnTask?,
    context: TaskContextIndex,
    onMarkComplete: () -> Unit,
    onReopen: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("TASK DETAIL", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            if (task == null) {
                Text("Select a task to see context and next actions.", color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                return@HavnCard
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(task.title, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall, modifier = Modifier.weight(1f))
                TaskStatusBadge(task.status)
            }
            Text(task.description ?: task.type.label, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TaskPriorityBadge(task.priority)
                SmallMeta(task.dueAt?.readableDateTime() ?: "No due date")
                if (task.aiGenerated) AiBadge()
            }
            TaskContextCard(
                property = context.properties[task.propertyId],
                openHouse = context.openHouses[task.openHouseId],
                lead = context.leads[task.leadId]
            )
            if (!task.notes.isNullOrBlank()) {
                Text("Notes", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                Text(task.notes, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            }
            TaskTimeline(task)
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                if (task.status == HavnTaskStatus.Done) {
                    HavnButton("Reopen", onClick = onReopen, style = HavnButtonStyle.Secondary, modifier = Modifier.weight(1f))
                } else {
                    HavnButton("Mark Complete", onClick = onMarkComplete, leadingIcon = HavnIconKey.Check, modifier = Modifier.weight(1f))
                }
                HavnButton("Edit Task", onClick = {}, style = HavnButtonStyle.Secondary, leadingIcon = HavnIconKey.Settings, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun TaskContextCard(property: Property?, openHouse: OpenHouse?, lead: Lead?) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.surface, shape = MaterialTheme.shapes.medium, border = BorderStroke(1.dp, theme.border), modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("Connected Context", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
            if (property == null && openHouse == null && lead == null) {
                Text("No property, opening, or lead is linked yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            }
            property?.let {
                ContextLine(HavnIconKey.Home, it.title, it.displayCity)
            }
            openHouse?.let {
                ContextLine(HavnIconKey.CalendarClock, it.displayTitle, it.startsAt.readableDateTime())
            }
            lead?.let {
                ContextLine(HavnIconKey.Leads, it.fullName, it.sourceLine)
            }
        }
    }
}

@Composable
private fun TaskTimeline(task: HavnTask) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("Activity Timeline", color = LocalHavnThemeTokens.current.primaryAccent, style = MaterialTheme.typography.labelLarge)
        TimelineLine("Created", task.createdAt?.readableDateTime() ?: "Recently")
        if (task.status == HavnTaskStatus.Done) {
            TimelineLine("Completed", task.completedAt?.readableDateTime() ?: "Completed")
        } else {
            TimelineLine("Next action", "Agent review or completion")
        }
    }
}

@Composable
private fun TimelineLine(label: String, value: String) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
        Surface(Modifier.size(8.dp), color = theme.primaryAccent, shape = CircleShape) {}
        Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
        Text(value, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
    }
}

@Composable
private fun ContextLine(icon: HavnIconKey, title: String, subtitle: String) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(9.dp), verticalAlignment = Alignment.CenterVertically) {
        HavnIcon(icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(17.dp))
        Column {
            Text(title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(subtitle, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun TaskPriorityBadge(priority: HavnTaskPriority) {
    val theme = LocalHavnThemeTokens.current
    val accent = when (priority) {
        HavnTaskPriority.Low -> theme.secondaryText
        HavnTaskPriority.Medium -> theme.primaryAccent
        HavnTaskPriority.High -> theme.warning
        HavnTaskPriority.Urgent -> theme.danger
    }
    Badge(priority.label, accent)
}

@Composable
private fun TaskStatusBadge(status: HavnTaskStatus) {
    val theme = LocalHavnThemeTokens.current
    val accent = when (status) {
        HavnTaskStatus.Done -> theme.success
        HavnTaskStatus.Overdue -> theme.danger
        HavnTaskStatus.InProgress -> theme.warning
        HavnTaskStatus.Cancelled -> theme.mutedText
        HavnTaskStatus.Open -> theme.primaryAccent
    }
    Badge(status.label, accent)
}

@Composable
private fun AiBadge() {
    Badge("AI", LocalHavnThemeTokens.current.secondaryAccent)
}

@Composable
private fun SmallMeta(text: String) {
    Badge(text, LocalHavnThemeTokens.current.secondaryText)
}

@Composable
private fun Badge(text: String, accent: Color) {
    Surface(color = accent.copy(alpha = 0.14f), shape = MaterialTheme.shapes.small, border = BorderStroke(1.dp, accent.copy(alpha = 0.45f))) {
        Text(text, color = accent, style = MaterialTheme.typography.labelSmall, modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp), maxLines = 1)
    }
}

private data class TaskContextIndex(
    val properties: Map<String, Property>,
    val openHouses: Map<String, OpenHouse>,
    val leads: Map<String, Lead>,
    val followUps: Map<String, FollowUp>,
    val members: Map<String?, AgencyMember>
) {
    fun propertyLabel(task: HavnTask): String? =
        properties[task.propertyId]?.title
            ?: leads[task.leadId]?.sourceProperty?.title
            ?: openHouses[task.openHouseId]?.propertyTitle

    fun leadLabel(task: HavnTask): String? = leads[task.leadId]?.fullName

    fun memberLabel(agentId: String?): String? = members[agentId]?.fullName
}

private enum class TaskFilter(val label: String, val emptyText: String) {
    Today("Today", "You're clear for today."),
    Upcoming("Upcoming", "No upcoming tasks."),
    Overdue("Overdue", "No overdue tasks."),
    Completed("Completed", "No completed tasks yet."),
    AssignedToMe("Assigned to Me", "No tasks assigned to you."),
    AiSuggested("AI Suggested", "No accepted AI suggested tasks yet.")
}

private fun TaskFilter.countFor(tasks: List<HavnTask>, suggestionCount: Int): Int = when (this) {
    TaskFilter.AiSuggested -> suggestionCount
    else -> tasks.filterBy(this).size
}

private fun List<HavnTask>.filterBy(filter: TaskFilter): List<HavnTask> = when (filter) {
    TaskFilter.Today -> filter { it.isToday() && it.status != HavnTaskStatus.Done }
    TaskFilter.Upcoming -> filter { !it.isToday() && !it.isOverdue() && it.status != HavnTaskStatus.Done }
    TaskFilter.Overdue -> filter { it.isOverdue() && it.status != HavnTaskStatus.Done }
    TaskFilter.Completed -> filter { it.status == HavnTaskStatus.Done }
    TaskFilter.AssignedToMe -> filter { it.status != HavnTaskStatus.Done }
    TaskFilter.AiSuggested -> filter { it.aiGenerated }
}.sortedWith(compareBy<HavnTask> { it.status == HavnTaskStatus.Done }.thenBy { it.dueAt ?: "9999" })

private fun buildAiSuggestions(
    leads: List<Lead>,
    openHouses: List<OpenHouse>,
    followUps: List<FollowUp>,
    agencyId: String?,
    dismissedIds: Set<String>
): List<AiSuggestedTask> {
    val suggestions = mutableListOf<AiSuggestedTask>()
    leads.filter { it.temperature == LeadTemperature.Hot }.take(3).forEach { lead ->
        suggestions += AiSuggestedTask(
            id = "hot-lead:${lead.id}",
            draft = HavnTaskDraft(
                title = "Follow up with ${lead.fullName} within 2 hours.",
                description = "Hot lead from ${lead.sourceProperty.title}. Keep momentum while intent is fresh.",
                type = HavnTaskType.FollowUpLead,
                priority = HavnTaskPriority.Urgent,
                dueAt = lead.checkedInAt,
                assignedToUserId = null,
                agencyId = agencyId,
                propertyId = lead.propertyId,
                openHouseId = lead.openHouseId,
                leadId = lead.id,
                aiGenerated = true
            ),
            reason = "New hot lead captured."
        )
    }
    openHouses.filter { it.status == OpenHouseStatus.Completed }.take(2).forEach { opening ->
        suggestions += AiSuggestedTask(
            id = "report:${opening.id}",
            draft = HavnTaskDraft(
                title = "Generate seller report for ${opening.propertyTitle}.",
                description = "Summarize visitor volume, hot leads, follow-up activity, and buyer sentiment.",
                type = HavnTaskType.PrepareSellerReport,
                priority = HavnTaskPriority.High,
                dueAt = opening.endsAt,
                agencyId = agencyId,
                propertyId = opening.propertyId,
                openHouseId = opening.id,
                aiGenerated = true
            ),
            reason = "Open house ended."
        )
    }
    followUps.filter { it.status != FollowUpStatus.Completed }.take(2).forEach { followUp ->
        suggestions += AiSuggestedTask(
            id = "review-follow-up:${followUp.id}",
            draft = HavnTaskDraft(
                title = "Review and send ${followUp.channel.label.lowercase()} follow-up.",
                description = followUp.subject,
                type = HavnTaskType.ReviewAiFollowUp,
                priority = HavnTaskPriority.Medium,
                dueAt = followUp.dueAt,
                agencyId = agencyId,
                followUpId = followUp.id,
                aiGenerated = true
            ),
            reason = "Follow-up draft still needs agent review."
        )
    }
    openHouses.filter { it.status == OpenHouseStatus.Scheduled }.take(2).forEach { opening ->
        suggestions += AiSuggestedTask(
            id = "kiosk:${opening.id}",
            draft = HavnTaskDraft(
                title = "Prepare kiosk mode and verify property photos.",
                description = "Confirm sign-in flow, gallery, and opening details before buyers arrive.",
                type = HavnTaskType.SetupKioskMode,
                priority = HavnTaskPriority.Medium,
                dueAt = opening.startsAt,
                agencyId = agencyId,
                propertyId = opening.propertyId,
                openHouseId = opening.id,
                aiGenerated = true
            ),
            reason = "Open house is scheduled."
        )
    }
    return suggestions
        .filterNot { it.id in dismissedIds || it.draft.suggestionKey() in dismissedIds }
        .distinctBy { it.id }
        .take(6)
}

private fun HavnTask.isToday(): Boolean = dueAt?.substringBefore("T") == todayIsoDate()

@OptIn(ExperimentalTime::class)
private fun HavnTask.isOverdue(): Boolean {
    val due = dueAt ?: return status == HavnTaskStatus.Overdue
    return status == HavnTaskStatus.Overdue || (status != HavnTaskStatus.Done && due < Clock.System.now().toString())
}

@OptIn(ExperimentalTime::class)
private fun todayIsoDate(): String = Clock.System.now().toString().substringBefore("T")

private fun String.readableDateTime(): String {
    val date = substringBefore("T")
    val time = substringAfter("T", "").substringBefore(".").substringBefore("+").substringBefore("Z").take(5)
    return if (time.isBlank()) date else "$date · $time"
}

private fun HavnTaskDraft.suggestionKey(): String =
    listOf(type.databaseValue, propertyId, openHouseId, leadId, followUpId, title).joinToString(":")
